import React, { useRef, useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { Menu } from "primereact/menu";
import { ConfirmDialog } from "primereact/confirmdialog";
import { useNavigate } from "react-router-dom";
import { Tag } from "primereact/tag";
import {
  deleteMultipleTenants,
  getTenants,
} from "../../store/AsyncMethods/TenantMethod";
import { useTranslation } from "react-i18next";

export default function TenantTable() {
  const { allTenants } = useSelector((state) => state.TenantReducer);
  const { user } = useSelector((state) => state.AuthReducer);
  const { t } = useTranslation();
  const menu = useRef(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showDelDialog, setShowDelDialog] = useState(false);
  const [showMultipleDelDialog, setShowMultipleDelDialog] = useState(false);
  const [selectedTenants, setSelectedTenants] = useState(null);

  useEffect(() => {
    if (user) {
      dispatch(getTenants());
    }
  }, [dispatch, user]);

  const accept = () => {
    dispatch(deleteMultipleTenants(selectedItem?.id)).then((success) => {
      if (success) {
        setSelectedItem(null);
        dispatch(getTenants());
      }
    });
  };

  const reject = () => {
    setShowDelDialog(false);
  };

  const acceptMultiple = () => {
    if (selectedTenants) {
      const idsArray = selectedTenants.map((obj) => obj.id);

      if (selectedTenants.length !== 0) {
        dispatch(deleteMultipleTenants(idsArray)).then((success) => {
          if (success) {
            setSelectedTenants(null);
            dispatch(getTenants());
          }
        });
      }
    }
  };

  const rejectMultiple = () => {
    setShowMultipleDelDialog(false);
  };

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const onGlobalFilterChange = (event) => {
    const value = event.target.value;
    let _filters = { ...filters };

    _filters["global"].value = value;

    setFilters(_filters);
  };

  const paginatorLeft = (
    <div className="mr-auto">
      <Button
        label="Delete"
        icon="pi pi-trash"
        className="p-red-btn !mr-auto"
        onClick={() => setShowMultipleDelDialog(true)}
      />
    </div>
  );

  const renderHeader = () => {
    const value = filters["global"] ? filters["global"].value : "";

    return (
      <div className="flex justify-between">
        <span className="p-input-icon-left flex w-10/12 sm:w-8/12 md:w-6/12 lg:w-4/12 xl:w-3/12 ">
          <InputText
            className="w-full p-primary-input"
            type="search"
            value={value || ""}
            onChange={(e) => onGlobalFilterChange(e)}
            placeholder="Search"
          />
        </span>
      </div>
    );
  };

  const header = renderHeader();

  let items = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: () => {
        navigate(`/edit-tenant/${selectedItem?.id}`);
      },
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: () => {
        setShowDelDialog(true);
      },
    },
  ];

  const createdByBody = (rowData) => {
    const findUser = allTenants.find((tenant) => tenant.id === rowData.created_by);
    return findUser ? `${findUser.first_name} ${findUser.last_name}` : "N/A";
  };
  
  const roleBodyTemplate = (rowData) => {
    return rowData.role_name || "N/A";
  };

  const statusBodyTemplate = (rowData) => {
    return (
      <Tag
        value={rowData.is_active ? t("active") : t("deactive")}
        pt={{
          root: {
            className: getSeverity(rowData),
          },
        }}
      />
    );
  };

  const getSeverity = (data) => {
    switch (data.is_active) {
      case 1:
      case true:
        return "bg-green";

      case 0:
      case false:
        return "bg-red";

      default:
        return null;
    }
  };

  const dateAndTimeBody = (rowData) => {
    const date = new Date(rowData.created_at);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear());
    let hour = date.getHours();
    const minute = String(date.getMinutes()).padStart(2, "0");
    let period = "AM";

    if (hour >= 12) {
      period = "PM";
      hour = hour === 12 ? hour : hour - 12;
    }

    hour = String(hour).padStart(2, "0");

    const formattedDate = `${day}-${month}-${year}, ${hour}:${minute} ${period}`;
    return formattedDate;
  };
  
  const actionBodyTemplate = (rowData) => {
    return (
        <Button 
            icon="pi pi-ellipsis-v" 
            className="p-button-rounded p-button-text" 
            onClick={(event) => {
                setSelectedItem(rowData);
                menu.current.toggle(event);
            }} 
        />
    );
  };


  return (
    <div>
      <Menu model={items} popup ref={menu} id="popup_menu" />
      <ConfirmDialog
        visible={showDelDialog}
        onHide={() => setShowDelDialog(false)}
        message="Do you want to delete this record?"
        header="Delete confirmation"
        icon="pi pi-info-circle"
        accept={accept}
        reject={reject}
        acceptLabel="Yes"
        pt={{
          acceptButton: { className: "p-red-btn" },
          rejectButton: { className: "p-primary-btn" },
          root: { className: "w-9/12 sm:w-7/12 md:w-5/12 lg:w-4/12 xl:w-3/12" },
          icon: { className: "mx-2" },
        }}
      />
      <ConfirmDialog
        visible={showMultipleDelDialog}
        onHide={() => setShowMultipleDelDialog(false)}
        message="Do you want to delete the selected records?"
        header="Delete confirmation"
        icon="pi pi-info-circle"
        accept={acceptMultiple}
        reject={rejectMultiple}
        acceptLabel="Yes"
        pt={{
          acceptButton: { className: "p-red-btn" },
          rejectButton: { className: "p-primary-btn" },
          root: { className: "w-9/12 sm:w-7/12 md:w-5/12 lg:w-4/12 xl:w-3/12" },
          icon: { className: "mx-2" },
        }}
      />
      <div className="card shadow-sm">
        <DataTable
          value={allTenants}
          tableStyle={{ minWidth: "50rem" }}
          stripedRows
          paginator
          rows={5}
          rowsPerPageOptions={[5, 10, 25, 50]}
          header={header}
          filters={filters}
          onFilter={(e) => setFilters(e.filters)}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          selection={selectedTenants}
          onSelectionChange={(e) => setSelectedTenants(e.value)}
          pt={{
            filterOperator: { className: "w-3" },
          }}
          onRowClick={(e) => navigate(`/edit-tenant/${e.data.id}`)}
          paginatorLeft={paginatorLeft}
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
        >
          <Column
            selectionMode="multiple"
            headerStyle={{ width: "3rem" }}
          ></Column>
          <Column
            field="first_name"
            sortable
            header="First Name"
            style={{ minWidth: "150px" }}
          />
          <Column
            field="last_name"
            sortable
            header="Last Name"
            style={{ minWidth: "150px" }}
          />
          <Column
            field="email"
            sortable
            header={t("email")}
            style={{ minWidth: "150px" }}
          />
           <Column
            field="role_name"
            header="Role"
            sortable
            body={roleBodyTemplate}
            style={{ minWidth: "120px" }}
          />
          {/* <Column
            header="Created By"
            style={{ minWidth: "150px" }}
            body={createdByBody}
            sortable
          /> */}
          <Column
            field="is_active"
            sortable
            header="Status"
            body={statusBodyTemplate}
            style={{ minWidth: "110px" }}
          />
          <Column
            field="created_at"
            sortable
            header="Date"
            body={dateAndTimeBody}
            style={{ minWidth: "170px" }}
          />
          {/* <Column header="Action" body={actionBodyTemplate} exportable={false} style={{ minWidth: '8rem' }} /> */}
        </DataTable>
      </div>
    </div>
  );
} 